// How a line says what it is a share OF.
//
// A Dial AI invoice to a channel partner asks for our revenue share, so the
// figure in the amount column is the net — and on its own it is unreadable. The
// partner is being asked for $1,225.00 against a customer who was charged
// $1,750.00, and nothing in "$1,225.00" says which customer, which rate, or that
// a rate was involved at all. The document has always closed that gap in WORDS:
//
//   Grace AI SaaS Subscription Fee ($1,750 @ 70% Rev Share)      1,225.00
//
// That parenthetical is the whole contract between this system and the person
// retyping the document into QuickBooks, and until migration 0024 it could only
// be written by hand, because the gross was not on the line to write.
//
// ONE IMPLEMENTATION, used by the invoice sheet's clipboard export and by the
// line editor's suggestion, so a line pasted into the accounting system and a
// line typed in this app cannot come out worded differently.

import type { InvoiceLineListItem } from "@crm/shared";
import type { Currency } from "@crm/shared";
import { formatMoney, formatPercent } from "./money.js";

/**
 * The `($1,750 @ 70% Rev Share)` half, or null when the line has no share to
 * describe.
 *
 * NULL RATHER THAN A PARTIAL PHRASE, on all three of the ways a line can fail to
 * have one: a standalone credit was not divided out of anything, a line nobody
 * has priced has no base yet, and a line whose split does not resolve has no
 * rate. Half a parenthetical — `($1,750 @ ...)` — would put a number on the
 * paper that the reader would finish in their head, and the number they would
 * finish it with is the one this system exists to stop being guessed.
 *
 * WHOLE DOLLARS on the base, deliberately: this is prose, it matches the
 * sixteen documents already issued, and the cents are in the amount column
 * beside it where they are the figure rather than the explanation. A base with
 * a fractional part keeps it — dropping real cents would make the arithmetic
 * fail to check out.
 */
export function revShareNote(
  line: Pick<InvoiceLineListItem, "gross_minor" | "applied_split_pct">,
  currency: Currency,
): string | null {
  const { gross_minor: gross, applied_split_pct: pct } = line;
  if (gross === null || pct === null) return null;

  const base =
    gross % 100 === 0
      ? formatMoney(gross, currency, { omitCurrencyCode: true }).replace(/\.00$/, "")
      : formatMoney(gross, currency, { omitCurrencyCode: true });
  return `(${base} @ ${formatPercent(pct)} Rev Share)`;
}

/**
 * The line's full wording for the document: its description with the share note
 * appended, or just the description when there is nothing to append.
 *
 * The description is NOT rewritten to include the note when a person has
 * already written one that says it themselves — this is the suggestion, and the
 * editor offers it rather than applying it, for the reason every other
 * pre-filled figure in this system is offered: what goes on a document is a
 * decision somebody makes.
 */
export function revShareDescription(
  line: Pick<InvoiceLineListItem, "gross_minor" | "applied_split_pct" | "resolved_description">,
  currency: Currency,
): string {
  const note = revShareNote(line, currency);
  return note === null ? line.resolved_description : `${line.resolved_description} ${note}`;
}
